import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FeesTable } from './FeesTable';

const PORTAL_TARGET_ID = 'seller-fee-table';
const OBSERVER_TIMEOUT_MS = 5000;

const PORTAL_TARGET_SELECTOR = `#${PORTAL_TARGET_ID}`;

export const FeesTablePortal = () => {
  const [portalTargets, setPortalTargets] = useState<Element[]>([]);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const syncTargets = () => {
      const targets = Array.from(document.querySelectorAll(PORTAL_TARGET_SELECTOR));

      setPortalTargets((current) => {
        const sameLength = current.length === targets.length;
        const unchanged = sameLength && targets.every((target, index) => current[index] === target);
        return unchanged ? current : targets;
      });
    };

    syncTargets();

    const disconnect = () => {
      observerRef.current?.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    const container = document.getElementById('main') ?? document.body;

    observerRef.current = new MutationObserver(syncTargets);
    observerRef.current.observe(container, { childList: true, subtree: true });

    timeoutRef.current = setTimeout(() => {
      observerRef.current?.disconnect();
    }, OBSERVER_TIMEOUT_MS);

    return disconnect;
  }, []);

  if (portalTargets.length === 0) {
    return null;
  }

  return (
    <>
      {portalTargets.map((target, index) => createPortal(
        <FeesTable />,
        target,
        // eslint-disable-next-line react/no-array-index-key
        `fees-table-${index}`,
      ))}
    </>
  );
};
